import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import styled from '@emotion/styled';
import { PokerTheme } from '../../../Themes/PokerThemes';
import potChip from '/images/table/pot-chip.svg';
import potChipAlt from '/images/table/pot-chip-alt.svg';
import { isMobile } from 'react-device-detect';
import styles from '../../../assets/css/components/Texas.module.css';
import '../../../assets/css/components/CardAnimations.css';
import { useTranslation } from 'react-i18next';

// Normal tables
import desktopTable from '/images/table/desktop-game-table.svg';
import mobileTable from '/images/table/mobile-game-table.svg';

// AoF tables
import desktopTableAofLow from '/images/table/desktop-game-table-low.svg';
import desktopTableAofMid from '/images/table/desktop-game-table-mid.svg';
import desktopTableAofHigh from '/images/table/desktop-game-table-high.svg';
import mobileTableAofLow from '/images/table/mobile-game-table-low.svg';
import mobileTableAofMid from '/images/table/mobile-game-table-mid.svg';
import mobileTableAofHigh from '/images/table/mobile-game-table-high.svg';

// Tournament tables
import desktopTableTournament from '/images/table/desktop-game-table-tournament.svg';
import desktopTableTournamentFinal from '/images/table/desktop-game-table-tournament-final.svg';
import mobileTableTournament from '/images/table/mobile-game-table-tournament.svg';
import mobileTableTournamentFinal from '/images/table/mobile-game-table-tournament-final.svg';

// Crypto tables
import auraTable from '/images/table/desktop-game-table-aura.svg';
import bnbTable from '/images/table/desktop-game-table-bnb.svg';
import busdTable from '/images/table/desktop-game-table-busd.svg';
import daiTable from '/images/table/desktop-game-table-dai.svg';
import dogeTable from '/images/table/desktop-game-table-doge.svg';
import dotTable from '/images/table/desktop-game-table-dot.svg';
import kltvTable from '/images/table/desktop-game-table-kltv.svg';
import oplTable from '/images/table/desktop-game-table-opl.svg';
import shibTable from '/images/table/desktop-game-table-shib.svg';
import solTable from '/images/table/desktop-game-table-sol.svg';
import solamaTable from '/images/table/desktop-game-table-solama.svg';
import usdcTable from '/images/table/desktop-game-table-usdc.svg';
import usdtTable from '/images/table/desktop-game-table-usdt.svg';
import vlxTable from '/images/table/desktop-game-table-vlx.svg';
import xmrTable from '/images/table/desktop-game-table-xmr.svg';

// Crypto mobile tables
import auraTableMobile from '/images/table/mobile-game-table-aura.svg';
import bnbTableMobile from '/images/table/mobile-game-table-bnb.svg';
import busdTableMobile from '/images/table/mobile-game-table-busd.svg';
import daiTableMobile from '/images/table/mobile-game-table-dai.svg';
import dogeTableMobile from '/images/table/mobile-game-table-doge.svg';
import dotTableMobile from '/images/table/mobile-game-table-dot.svg';
import kltvTableMobile from '/images/table/mobile-game-table-kltv.svg';
import oplTableMobile from '/images/table/mobile-game-table-opl.svg';
import shibTableMobile from '/images/table/mobile-game-table-shib.svg';
import solTableMobile from '/images/table/mobile-game-table-sol.svg';
import solamaTableMobile from '/images/table/mobile-game-table-solama.svg';
import usdcTableMobile from '/images/table/mobile-game-table-usdc.svg';
import usdtTableMobile from '/images/table/mobile-game-table-usdt.svg';
import vlxTableMobile from '/images/table/mobile-game-table-vlx.svg';
import xmrTableMobile from '/images/table/mobile-game-table-xmr.svg';
import tableTrophy from '/images/table/table-trophy.png';
import { IPlayerState } from '../../../Core/State/IPlayerState';
import { computeTexasRealTimeTotalPot } from '../../../Utils/PokerPotUtils';
import { HAND_PRESENTATION_FADE_OUT_MS } from '../Presentation/PokerPresentationConfig';
import {
    getPokerTableScaleStyle,
    usePokerTableResponsiveScale
} from '../Presentation/PokerTableResponsiveScale';

interface PokerTableWrapperProps {
    type?: string;
    currencytype?: string;
    currency?: string;
    stakelevel?: string;
    isTournamentTable?: boolean;
    isFinalTournamentTable?: boolean;
}

const PokerTableWrapper = styled(Box, {
    shouldForwardProp: (prop) => !['type', 'currencytype', 'currency', 'stakelevel', 'isTournamentTable', 'isFinalTournamentTable'].includes(prop as string)
})<PokerTableWrapperProps>(({ theme, type, currencytype, currency, stakelevel, isTournamentTable, isFinalTournamentTable }) => {
    const pokerTheme = theme as PokerTheme;
    let tableBg = "";

    if (isFinalTournamentTable) {
        tableBg = isMobile ? mobileTableTournamentFinal : desktopTableTournamentFinal;
    } else if (isTournamentTable) {
        tableBg = isMobile ? mobileTableTournament : desktopTableTournament;
    } else if (type === 'texas') {
        tableBg = isMobile ? mobileTable : desktopTable;
    } else if (type === 'aof') {
        if (stakelevel === 'low') {
            tableBg = isMobile ? mobileTableAofLow : desktopTableAofLow;
        } else if (stakelevel === 'mid') {
            tableBg = isMobile ? mobileTableAofMid : desktopTableAofMid;
        } else if (stakelevel === 'high') {
            tableBg = isMobile ? mobileTableAofHigh : desktopTableAofHigh;
        }
        if (currencytype === 'crypto' && currency) {
            switch (currency.toLowerCase()) {
                case 'aura':
                    tableBg = isMobile ? auraTableMobile : auraTable;
                    break;
                case 'bnb':
                    tableBg = isMobile ? bnbTableMobile : bnbTable;
                    break;
                case 'busd':
                    tableBg = isMobile ? busdTableMobile : busdTable;
                    break;
                case 'dai':
                    tableBg = isMobile ? daiTableMobile : daiTable;
                    break;
                case 'doge':
                    tableBg = isMobile ? dogeTableMobile : dogeTable;
                    break;
                case 'dot':
                    tableBg = isMobile ? dotTableMobile : dotTable;
                    break;
                case 'kltv':
                    tableBg = isMobile ? kltvTableMobile : kltvTable;
                    break;
                case 'opl':
                    tableBg = isMobile ? oplTableMobile : oplTable;
                    break;
                case 'shib':
                    tableBg = isMobile ? shibTableMobile : shibTable;
                    break;
                case 'sol':
                    tableBg = isMobile ? solTableMobile : solTable;
                    break;
                case 'solama':
                    tableBg = isMobile ? solamaTableMobile : solamaTable;
                    break;
                case 'usdc':
                    tableBg = isMobile ? usdcTableMobile : usdcTable;
                    break;
                case 'usdt':
                    tableBg = isMobile ? usdtTableMobile : usdtTable;
                    break;
                case 'vlx':
                    tableBg = isMobile ? vlxTableMobile : vlxTable;
                    break;
                case 'xmr':
                    tableBg = isMobile ? xmrTableMobile : xmrTable;
                    break;
                default:
                    tableBg = isMobile ? mobileTableAofLow : desktopTableAofLow;
            }
        }
    }

    return {
        maxWidth: '100%',
        backgroundImage: `url(${tableBg})`,
        backgroundSize: '100% 100%',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        border: pokerTheme.poker.tableBorder,
        boxShadow: pokerTheme.poker.tableBoxShadow,
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        boxSizing: 'border-box',
        margin: isMobile ? '40px auto auto auto' : 'auto',
        aspectRatio: pokerTheme.poker.tableAspectRatio,
        height: pokerTheme.poker.tableHeight,
        width: pokerTheme.poker.tableWidth
    };
});

const TopPotHolder = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isTournamentTable'
})<{ isTournamentTable?: boolean }>(({ isTournamentTable }) => {
    return {
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        top: isMobile ? (isTournamentTable ? '26%' : '32%') : '20%',
        left: '50%',
        transform: isMobile ? 'translateX(-50%)' : 'translateX(-50%) scale(var(--poker-ui-scale, 1))',
        transformOrigin: 'center top',
        zIndex: 10,
        gap: '4px',
        alignItems: 'center'
    };
});

const PotHolder = styled('div')(({ theme }) => {
    return {
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        position: 'absolute',
        top: isMobile ? '50%' : 'initial',
        bottom: isMobile ? 'initial' : '40%',
        left: '50%',
        transform: isMobile ? 'translateX(-50%)' : 'translateX(-50%) scale(var(--poker-ui-scale, 1))',
        transformOrigin: 'center center',
        zIndex: 10,
        gap: '4px',
        alignItems: 'center'
    };
});

const PotInfo = styled(Typography)(({ theme }) => {
    const pokerTheme = theme as PokerTheme;
    return {
        position: 'relative',
        backgroundColor: '#3E414D',
        borderRadius: '30px',
        padding: '2px 8px 2px 5px',
        color: '#FFFFFF',
        fontFamily: 'Inter, serif',
        fontSize: '14px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        zIndex: 10,
        '&:hover .eligible-info': {
            top: '100%',
            opacity: 1
        }
    };
});

const CommunityCardContainer = styled('div')<{ $position: number }>(({ $position }) => ({
    width: isMobile ? '36px' : '18.4%',
    height: '100%',
}));

const CommunityCardsArea = styled(Box, {
    shouldForwardProp: (prop) => prop !== '$isFadingOut'
})<{ $isFadingOut?: boolean }>(({ $isFadingOut }) => ({
    display: 'flex',
    justifyContent: 'center',
    gap: isMobile ? '10px' : 'calc(10px * var(--poker-table-scale, 1))',
    position: 'absolute',
    top: isMobile ? '36%' : '30%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: isMobile ? '200px' : 'calc(320px * var(--poker-table-scale, 1))',
    height: isMobile ? '78px' : 'calc(78px * var(--poker-table-scale, 1))',
    perspective: '1000px',
    zIndex: 10,
    opacity: $isFadingOut ? 0 : 1,
    transition: `opacity ${HAND_PRESENTATION_FADE_OUT_MS}ms ease-out`,
    pointerEvents: 'none'
}));

export enum CardAnimationStyle {
    NONE = 'none',
    FLIP = 'flip',
    SLIDE = 'slide',
    SPREAD = 'spread',
    DEAL1 = 'deal1'
}

interface SidePot {
    amount: number;
    eligiblePlayerIds: string[];
    contributors: Array<{
        playerId: string;
        username: string;
        amount: number;
    }>;
}

// Add this styled component for side pots
const SidePotInfo = styled('span')(({ theme }) => {
    const pokerTheme = theme as PokerTheme;
    return {
        position: 'relative',
        backgroundColor: '#3E414D',
        borderRadius: '30px',
        padding: '2px 8px 2px 5px',
        color: pokerTheme.poker.potInfoColor,
        fontFamily: 'Inter, serif',
        fontSize: '12px',
        fontWeight: 600,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        '& .pot-amount': {
            display: 'flex',
            alignItems: 'center',
            '& span': {
                color: pokerTheme.poker.potValueColor,
            }
        },
        '& .eligible-players': {
            position: 'relative',
            fontSize: '12px',
            color: '#FFFFFF',
            padding: '2px 4px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '15px',
            fontWeight: 600,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            textAlign: 'center'
        },
        '&:hover .eligible-info': {
            top: '100%',
            opacity: 1
        }
    };
});

const SidePotsContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    // top: isMobile ? 'calc(33% + 25px)' : 'calc(60% + 5px)',
    // left: '50%',
    // transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: isMobile ? '90%' : '350px',
    gap: '4px'
}));

const EligiblePlayersInfo = styled('span')(({ theme }) => {
    const pokerTheme = theme as PokerTheme;
    return {
        opacity: 0,
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'center',
        maxWidth: isMobile ? '90%' : '350px',
        transition: 'all 0.3s ease-in-out',
        '& .eligible-players': {
            position: 'relative',
            fontSize: '12px',
            color: '#FFFFFF',
            padding: '2px 4px',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '15px',
            fontWeight: 600,
            wordWrap: 'break-word',
            whiteSpace: 'normal',
            textAlign: 'center',
            transition: 'all 0.3s ease-in-out'
        }
    };
});

const TableInfo = styled(Stack, {
    shouldForwardProp: (prop) => !['isTournamentTable', 'stakeLevel', 'type', 'currencyType'].includes(prop as string)
})<{ isTournamentTable?: boolean; stakeLevel?: string; type?: string; currencyType?: string }>(({ isTournamentTable, stakeLevel, type, currencyType }) => ({
    position: 'absolute',
    top: isMobile ? '40%' : 'initial',
    bottom: isMobile ? 'initial' : '25%',
    justifyContent: isTournamentTable ? 'center' : 'initial',
    left: '50%',
    transform: isMobile ? 'translateX(-50%)' : 'translateX(-50%) scale(var(--poker-ui-scale, 1))',
    transformOrigin: 'center center',
    zIndex: '-1',
    gap: type === 'aof' && currencyType !== 'crypto' ? (stakeLevel === 'low' ? '52px' : stakeLevel === 'mid' ? '108px' : '154px') : '24px',
    width: '60%'
}));

const TableInfoText = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'isTournamentTable'
})<{ isTournamentTable?: boolean }>(({ isTournamentTable }) => {
    return {
        fontWeight: 600,
        opacity: 0.5,
        fontSize: '14px',
        textAlign: 'center',
        width: isMobile ? '100%' : isTournamentTable ? 'initial' :'50%',
        margin: '0 !important'
    };
});

interface PokerTableProps {
    children: React.ReactNode;
    pot: number;
    communityCards: string[];
    showAnimatedChip: boolean;
    formatCurrency: (amount: number) => string;
    renderCardImage: (card: string, small: boolean, community: boolean) => React.ReactNode;
    foldedPlayers: string[];
    theme: PokerTheme;
    cardAnimationStyle?: CardAnimationStyle;
    animationSpeed?: number;
    animatedCommunityCardDelaysMs?: { [cardIndex: number]: number };
    type?: string;
    stateName?: string;
    currencyType?: string;
    currency?: string;
    stakeLevel?: string;
    players?: IPlayerState[];
    visiblePot?: number;
    sidePots?: SidePot[];
    eligiblePotPlayers?: any[];
    tableName: string;
    ante: number;
    smallBlind: number;
    bigBlind: number;
    isTournamentTable?: boolean;
    isFinalTournamentTable?: boolean;
    isHandPresentationFadingOut?: boolean;
}

const PokerTable: React.FC<PokerTableProps> = ({
    children,
    pot,
    communityCards,
    showAnimatedChip,
    formatCurrency,
    renderCardImage,
    foldedPlayers,
    theme,
    cardAnimationStyle = CardAnimationStyle.FLIP,
    animationSpeed = 3,
    animatedCommunityCardDelaysMs = {},
    type,
    stateName,
    currencyType,
    currency,
    stakeLevel,
    players,
    visiblePot = 0,
    sidePots = [],
    eligiblePotPlayers = [],
    tableName,
    ante,
    smallBlind,
    bigBlind,
    isTournamentTable,
    isFinalTournamentTable,
    isHandPresentationFadingOut
}) => {
    const { t } = useTranslation('GameRoom');
    const tableRef = React.useRef<HTMLDivElement | null>(null);
    const tableScale = usePokerTableResponsiveScale(tableRef, !isMobile);
    const animatedCommunityCardIndexSet = React.useMemo(
        () => new Set(Object.keys(animatedCommunityCardDelaysMs).map(Number)),
        [animatedCommunityCardDelaysMs]
    );

    const tableScaleStyle = React.useMemo(
        () => getPokerTableScaleStyle(tableScale),
        [tableScale]
    );

    const getCardPosition = (index: number): number => {
        if (type === 'aof') {
            if (index < 3) return index;
            if (index === 3) return 3;
            return 4;
        }
        return index;
    };

    const getCardStyle = (index: number): React.CSSProperties => {
        const style = { '--x': `${index * 20}%` } as React.CSSProperties;
        const animationDelayMs = animatedCommunityCardDelaysMs[index];
        if (typeof animationDelayMs === 'number') {
            return {
                ...style,
                '--delay': `${animationDelayMs}ms`
            } as React.CSSProperties;
        }
        return style;
    };

    const getPot = () => {
        if (type === 'texas') {
            // Preflop/dealing: show posted blinds only (not live calls/raises)
            if ((stateName === 'preFlop' || stateName === 'dealing') && (!visiblePot || visiblePot === 0)) {
                const sb = Number(smallBlind || 0);
                const bb = Number(bigBlind || 0);
                const blindsTotal = sb + bb;
                if (blindsTotal > 0) return blindsTotal;
            }
            // Otherwise: current-round pot shows only consolidated chips (no current street bets)
            return visiblePot;
        }

        return pot;
    };
    
    // Total pot (live) for Texas
    const getTotalPot = () => computeTexasRealTimeTotalPot(visiblePot, sidePots, players);

    return (
        <PokerTableWrapper
            ref={tableRef}
            theme={theme}
            style={tableScaleStyle}
            className={isMobile ? styles.mobileTable : styles.desktopTable}
            type={type}
            currencytype={currencyType}
            currency={currency}
            stakelevel={stakeLevel}
            isTournamentTable={isTournamentTable}
            isFinalTournamentTable={isFinalTournamentTable}
        >
            {isFinalTournamentTable && !isMobile && (
                <img src={tableTrophy} alt="Trophy" style={{ position: 'absolute', bottom: '78%', left: '50%', transform: 'translateX(-50%)', width: 'clamp(100px, 25vw, 450px)', height: 'auto' }} />
            )}
            {children}
            {/* Top row: Total pot real time (Texas) */}
            {type === 'texas' && (
                <TopPotHolder isTournamentTable={isTournamentTable}>
                    <PotInfo style={{ backgroundColor: 'transparent' }}>
                        {t('gameInfo.labels.pot')}: {formatCurrency(getTotalPot())}
                    </PotInfo>
                </TopPotHolder>
            )}
            { getPot() > 0 && (
                <PotHolder>
                    {/* Bottom row: Current round pot */}
                    <PotInfo>
                        <img src={potChip} alt="PokerSolutions chip" style={{ marginRight: '6px' }} />
                        <span style={{ color: '#3AD65D' }}>{formatCurrency(getPot())}</span>
                        {/* Display eligible players if they exist */}
                        {eligiblePotPlayers && eligiblePotPlayers.length > 0 && !isMobile && (
                            <EligiblePlayersInfo className="eligible-info">
                                <span className="eligible-players" style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                    {players
                                        ?.filter(player => eligiblePotPlayers.includes(player.userId))
                                        .map(player => (
                                            <img
                                                key={player.userId}
                                                src={player.avatarUrl || `${import.meta.env.BASE_URL}images/table/avatar.svg`}
                                                alt={player.username || player.userId.substring(0, 6)}
                                                title={player.username || player.userId.substring(0, 6)}
                                                style={{
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: '50%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        ))}
                                </span>
                            </EligiblePlayersInfo>
                        )}
                    </PotInfo>
                
                    {/* Display side pots if they exist */}
                    {sidePots && sidePots.length > 0 && !isMobile && !isTournamentTable && (
                        <SidePotsContainer>
                            {sidePots.map((sidePot, index) => {
                                const eligiblePlayers = players
                                    ?.filter(player => sidePot.eligiblePlayerIds.includes(player.userId))
                                    .map(player => player.username || player.userId.substring(0, 6))
                                    .join(', ');
                                    
                                return (
                                    <SidePotInfo key={`sidepot-${index}`}>
                                        <div className="pot-amount">
                                            <img src={potChipAlt} alt="PokerSolutions chip" style={{ marginRight: '6px', width: '16px', height: '16px' }} />
                                            <span>{formatCurrency(sidePot.amount)}</span>
                                        </div>
                                        {eligiblePlayers && (
                                            <EligiblePlayersInfo className="eligible-info">
                                                <span className="eligible-players" style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                                                    {players
                                                        ?.filter(player => eligiblePotPlayers.includes(player.userId))
                                                        .map(player => (
                                                            <img
                                                                key={player.userId}
                                                                src={player.avatarUrl || `${import.meta.env.BASE_URL}images/table/avatar.svg`}
                                                                alt={player.username || player.userId.substring(0, 6)}
                                                                title={player.username || player.userId.substring(0, 6)}
                                                                style={{
                                                                    width: 16,
                                                                    height: 16,
                                                                    borderRadius: '50%',
                                                                    objectFit: 'cover'
                                                                }}
                                                            />
                                                        ))}
                                                </span>
                                            </EligiblePlayersInfo>
                                        )}
                                    </SidePotInfo>
                                );
                            })}
                        </SidePotsContainer>
                    )}
                </PotHolder>
            )}
            
            <CommunityCardsArea $isFadingOut={isHandPresentationFadingOut}>
                {communityCards.map((card, index) => (
                    <CommunityCardContainer
                        key={`community-${index}-${card}`}
                        $position={getCardPosition(index)}
                        className={`community-card-container ${
                            animatedCommunityCardIndexSet.has(index)
                                ? 'community-card-container--animated'
                                : ''
                        }`}
                        style={getCardStyle(index)}
                    >
                        <div className="card">
                            <img className="card-back" src={`${import.meta.env.BASE_URL}images/deck-01/1B.svg`} alt="Card Back" style={{ width: '100%' }} />
                            {renderCardImage(card, false, true)}
                        </div>
                    </CommunityCardContainer>
                ))}
            </CommunityCardsArea>
            <TableInfo direction={isMobile ? "column" : "row"} spacing={isMobile ? 0 : 3} isTournamentTable={isTournamentTable} stakeLevel={stakeLevel} type={type} currencyType={currencyType}>
                {!isMobile && (<TableInfoText style={{ textAlign: 'right' }} isTournamentTable={isTournamentTable}>{t('gameInfo.labels.table')}: {tableName}</TableInfoText>)}
                {type === 'aof' ? (
                    <TableInfoText style={{ textAlign: isMobile ? 'center' : 'left' }}>{t('gameInfo.labels.ante')}: {formatCurrency(ante)}</TableInfoText>
                ) : (
                    <TableInfoText style={{ textAlign: isMobile ? 'center' : 'left' }} isTournamentTable={isTournamentTable}>{t('gameInfo.labels.blinds')}: {formatCurrency(smallBlind)} / {formatCurrency(bigBlind)}</TableInfoText>
                )}
            </TableInfo>
        </PokerTableWrapper>
    );
};

export default PokerTable;
